import React, {ReactNode} from 'react'
import styled, {css} from 'styled-components'
import {Icon, IconName} from '../icon/icon'

const Label = styled.div<{inline?: boolean}>`
  color: ${({theme}) => theme.grey50};
  line-height: ${({theme}) => theme.lineHeight};
  font-size: ${({theme}) => theme.fontSize};
  position: absolute;
  left: 20px;
  top: 20px;
  transition: 0.1s margin-top, 0.1s font-size;
  ${p =>
    p.inline
      ? `
        left: 15px;
        top: 10px;`
      : `
        left: 20px;
        top: 20px;`}
`
const Value = styled.div<{blue?: boolean; readOnly?: boolean}>`
  ${p =>
    p.readOnly
      ? `color: ${p.theme.colors.grey10};`
      : p.blue
      ? `color: ${p.theme.blue};`
      : `color: ${p.theme.black};`}
  line-height: ${({theme}) => theme.lineHeight};
  font-size: ${({theme}) => theme.fontSize};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const IconRight = styled.span<{
  blue?: boolean
  inline?: boolean
  readOnly?: boolean
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex: none;
  ${p =>
    p.inline
      ? `width: 40px;
        height: 40px;`
      : `width: 60px;
        height: 60px;`}
  ${p =>
    p.readOnly
      ? `color: ${p.theme.colors.grey10};`
      : p.blue
      ? `color: ${p.theme.blue};`
      : null}
`
const IconLeft = styled.span<{blue?: boolean; inline?: boolean}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex: none;
  ${p =>
    p.inline
      ? `width: 40px;
        height: 40px;`
      : `width: 60px;
        height: 60px;`}
  ${p => (p.blue ? `color: ${p.theme.blue};` : null)}
`

const SelectWrapper = styled.div<{matchContent?: boolean; readOnly?: boolean}>`
  border-radius: ${({theme}) => theme.borderRadius};
  ${({readOnly, theme}) =>
    readOnly
      ? css`
          background: ${theme.white};
          border: solid 1px ${theme.grey20};
        `
      : css`
          background: ${theme.grey05};
          &:focus-within,
          &:hover {
            background-color: ${theme.grey10};
          }
          &:active {
            background-color: ${theme.grey15};
          }
        `}
  ${({matchContent}) =>
    matchContent &&
    css`
      display: inline-flex;
    `}
`
const SelectContent = styled.div<{
  iconLeft?: IconName
  inline?: boolean
  matchContent?: boolean
  readOnly?: boolean
}>`
  outline: none;
  padding-top: 10px;
  padding-bottom: 10px;
  position: relative;
  cursor: ${p => (p.readOnly ? `not-allowed` : `pointer`)};
  height: ${p => (p.inline ? `40px` : `60px`)};
  display: flex;
  align-items: center;

  ${Label} {
    ${p =>
      p.iconLeft
        ? p.inline
          ? p.matchContent
            ? 'left: 0px;'
            : `left: 40px;`
          : `left: 60px;`
        : p.inline
        ? `left: 15px;`
        : `left: 20px;`}
    ${p => (p.matchContent && p.inline ? 'top: 0; position: relative;' : null)}
  }

  ${Value} {
    ${p => (p.matchContent ? null : `margin-right: auto;`)}
    transition: 0.1s margin-top;
    ${p =>
      p.iconLeft
        ? `padding: 0px;`
        : p.inline
        ? `padding: 0 0 0 15px;`
        : `padding: 0 0 0 20px;`}
  }

  &.labelUp {
    ${Label} {
      ${p =>
        p.inline
          ? `opacity: 0;`
          : `
          margin-top: -10px;
          font-size: ${p.theme.fontSizeS};
          `}
      ${p => (p.matchContent && p.inline ? 'position: absolute;' : null)}
    }
    ${Value} {
      ${p => (p.inline ? null : `margin-top: 20px;`)}
    }
  }
`

const SecondaryButton = styled.div`
  position: relative;
  top: -50px;
  left: -40px;
  float: right;
  // override inherrited margin-bottom of 1px to remove empty
  // space left by relative positioning
  margin-bottom: -49px !important;
`

interface DropdownTitleProps {
  handleClick?: () => void
  ref: React.Ref<HTMLElement>
  value?: ReactNode
  label: string
  upLabel?: string
  tabIndex?: number
  iconLeft?: IconName
  iconRight?: IconName
  secondaryButtonRight?: React.ReactNode
  inline?: boolean
  matchContent?: boolean
  blue?: boolean
  style?: React.CSSProperties
  readOnly?: boolean
}

export const DropdownTitle = React.forwardRef<HTMLElement, DropdownTitleProps>(
  (
    {
      inline,
      handleClick,
      value,
      label,
      upLabel,
      tabIndex = 0,
      iconLeft,
      iconRight,
      secondaryButtonRight,
      matchContent,
      blue,
      style,
      readOnly
    },
    ref
  ) => {
    return (
      <>
        <SelectWrapper
          matchContent={matchContent}
          style={style}
          readOnly={readOnly}
        >
          <SelectContent
            readOnly={readOnly}
            data-testid={
              label
                ? `dropdown-${label
                    .toLowerCase()
                    .replace(/ /g, '-')
                    .replace(/[^a-zA-Z0-9-]/g, '')}`
                : undefined
            }
            inline={inline}
            matchContent={matchContent}
            iconLeft={iconLeft}
            className={value ? 'labelUp' : ''}
            ref={ref as React.Ref<HTMLDivElement>}
            onClick={readOnly ? undefined : handleClick}
            tabIndex={tabIndex}
          >
            {iconLeft && (
              <IconLeft blue={blue} inline={inline}>
                <Icon name={iconLeft} />
              </IconLeft>
            )}
            <Label inline={inline}>
              {value ? (upLabel ? upLabel : label) : label}
            </Label>
            <Value blue={blue} readOnly={readOnly}>
              {value}
            </Value>
            {handleClick && (
              <IconRight blue={blue} readOnly={readOnly} inline={inline}>
                <Icon name={iconRight ? iconRight : 'arrow_down'} />
              </IconRight>
            )}
          </SelectContent>
        </SelectWrapper>
        {/* Outside the dropdown but moved in with CSS, so clicking it doesn't open/close the dropdown */}
        {secondaryButtonRight ? (
          <SecondaryButton>{secondaryButtonRight}</SecondaryButton>
        ) : null}
      </>
    )
  }
)
